import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/utils/AuthContext';
import { login, requestPasswordReset, type AuthSession } from '@/utils/auth-pb';
import { checkEmailTypo } from '@/utils/email-typo-check';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { AuthInput } from '@/components/ui/auth-input';
import { PasswordInput } from '@/components/ui/password-input';
import { TelegramBotLoginButton } from '@/components/ui/TelegramBotLoginButton';
import LocalizedLink from '@/components/shared/LocalizedLink';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const { login: setSession } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const emailTypoHint = checkEmailTypo(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetSent(false);
    setIsLoading(true);

    try {
      const session = await login(email, password);
      setSession(session);
    } catch (err) {
      setError(t('login.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTelegramAuth = (session: AuthSession) => {
    setError('');
    setResetSent(false);
    setSession(session);
  };

  const handleTelegramError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError(t('login.enterEmailForReset'));
      return;
    }

    setError('');
    setIsResetting(true);

    try {
      await requestPasswordReset(email);
      setResetSent(true);
    } catch (err) {
      setError((err as { message: string }).message || t('login.resetSendError'));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <AuthLayout title={t('login.title')}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="rounded-[12px] bg-[#fff0f0] p-4 text-[14px] text-[#ff5b63]">
            {error}
          </div>
        )}

        {resetSent && (
          <div className="rounded-[12px] bg-[#e8f8e8] p-4 text-[14px] text-[#4caf50]">
            {t('login.resetSent')}
          </div>
        )}

        <AuthInput
          id="email"
          type="email"
          label={t('login.emailLabel')}
          placeholder={t('login.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        {emailTypoHint && (
          <p className="text-[12px] text-[#f59e0b]">{t('emailTypoCheck.suggestion', { suggestion: emailTypoHint, ns: 'common' })}</p>
        )}

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="block text-[14px] font-medium text-[#0d0d0d]"
          >
            {t('login.passwordLabel')}
          </label>
          <PasswordInput
            id="password"
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-6 h-[52px] w-full rounded-[12px] bg-[#2197f1] text-[16px] font-semibold text-white transition-colors hover:bg-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? t('login.submitting') : t('login.submit')}
        </button>

        <div className="space-y-3 pt-4">
          <p className="text-center text-[14px] text-[#565858]">
            {t('login.noAccount')}{' '}
            <LocalizedLink to="/register" className="font-semibold text-[#2197f1] hover:underline">
              {t('login.register')}
            </LocalizedLink>
          </p>

          <p className="text-center text-[14px] text-[#565858]">
            {t('login.forgotPassword')}{' '}
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={isResetting}
              className="font-semibold text-[#2197f1] hover:underline disabled:opacity-50"
            >
              {isResetting ? t('login.sendingResetLink') : t('login.sendResetLink')}
            </button>
          </p>
        </div>
      </form>

      <div className="space-y-3 pt-4">
        <div className="relative flex items-center">
          <div className="flex-grow border-t border-[#eeeeee]" />
          <span className="flex-shrink px-4 text-[13px] text-[#565858]">{t('login.or')}</span>
          <div className="flex-grow border-t border-[#eeeeee]" />
        </div>

        <TelegramBotLoginButton
          onAuth={handleTelegramAuth}
          onError={handleTelegramError}
          size="large"
        />
      </div>
    </AuthLayout>
  );
}
